import { useMutation } from "@tanstack/react-query";
import { AlertTriangle, MessageSquare, Send } from "lucide-react";
import {
  useDataProvider,
  useGetList,
  useListContext,
  useNotify,
} from "ra-core";
import { useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import type { Contact, MessageTemplate } from "../types";
import type { CrmDataProvider } from "../providers/types";

function substituteVariables(template: string, contact: Contact): string {
  return template.replace(/\{\{(\w+)\}\}/g, (match, variable) => {
    const value = contact[variable as keyof Contact];
    return value != null ? String(value) : match;
  });
}

export const BulkSendMessageButton = () => {
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setDialogOpen(true)}>
        <MessageSquare className="h-4 w-4" />
        Send message
      </Button>
      <BulkSendDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </>
  );
};

const BulkSendDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const {
    selectedIds,
    data: contacts,
    onUnselectItems,
  } = useListContext<Contact>();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();

  const [templateId, setTemplateId] = useState<string>("");
  const [channel, setChannel] = useState<"sms" | "whatsapp">("sms");

  const { data: templates } = useGetList<MessageTemplate>(
    "messageTemplates",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "name", order: "ASC" },
    },
    { enabled: open },
  );

  const selectedTemplate = templates?.find((t) => String(t.id) === templateId);

  const selectedContacts =
    contacts?.filter((c) => selectedIds.includes(c.id)) ?? [];

  const contactsWithPhone = selectedContacts.filter(
    (c) => c.phone_jsonb?.length > 0,
  );
  const contactsWithoutPhone = selectedContacts.filter(
    (c) => !c.phone_jsonb?.length,
  );

  const previewContact = contactsWithPhone[0];
  const previewMessage =
    selectedTemplate && previewContact
      ? substituteVariables(selectedTemplate.body, previewContact)
      : null;

  const { mutate: sendMessages, isPending } = useMutation({
    mutationKey: ["sendBulkMessage"],
    mutationFn: () =>
      dataProvider.sendBulkMessage({
        template_id: templateId,
        contact_ids: contactsWithPhone.map((c) => c.id),
        channel,
      }),
    onSuccess: (result) => {
      const message =
        result.failed > 0
          ? `${result.sent} messages sent, ${result.failed} failed`
          : `${result.sent} messages sent successfully`;
      notify(message, {
        type: result.failed > 0 ? "warning" : "success",
      });
      onUnselectItems();
      onClose();
    },
    onError: () => {
      notify("Failed to send messages", { type: "error" });
    },
  });

  const handleSend = () => {
    if (!templateId || contactsWithPhone.length === 0) return;
    sendMessages();
  };

  const handleTemplateChange = (value: string) => {
    setTemplateId(value);
    const template = templates?.find((t) => String(t.id) === value);
    if (template) {
      setChannel(template.channel);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Send message</DialogTitle>
          <DialogDescription>
            Send a message to {selectedIds.length} selected contact
            {selectedIds.length !== 1 ? "s" : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Template selection */}
          <div className="space-y-2">
            <Label>Template</Label>
            <Select value={templateId} onValueChange={handleTemplateChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select a template" />
              </SelectTrigger>
              <SelectContent>
                {templates?.map((template) => (
                  <SelectItem key={template.id} value={String(template.id)}>
                    {template.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {templates?.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No templates found. Create one from the Templates page first.
              </p>
            )}
          </div>

          {/* Channel selection */}
          {selectedTemplate && (
            <div className="space-y-2">
              <Label>Channel</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="send-channel"
                    value="sms"
                    checked={channel === "sms"}
                    onChange={() => setChannel("sms")}
                    className="accent-primary"
                  />
                  <span className="text-sm">SMS</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="send-channel"
                    value="whatsapp"
                    checked={channel === "whatsapp"}
                    onChange={() => setChannel("whatsapp")}
                    className="accent-primary"
                  />
                  <span className="text-sm">WhatsApp</span>
                </label>
              </div>
            </div>
          )}

          {/* Preview */}
          {previewMessage && (
            <div className="space-y-2">
              <Label>Preview</Label>
              <div className="rounded-md border p-3 bg-muted/50">
                <p className="text-sm whitespace-pre-wrap">{previewMessage}</p>
                <p className="text-xs text-muted-foreground mt-2">
                  Preview for: {previewContact.first_name}{" "}
                  {previewContact.last_name}
                </p>
              </div>
            </div>
          )}

          {/* Summary */}
          {selectedTemplate && (
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">
                {contactsWithPhone.length} recipient
                {contactsWithPhone.length !== 1 ? "s" : ""}
              </Badge>
              <Badge
                variant="outline"
                className={
                  channel === "whatsapp"
                    ? "border-green-500 text-green-700 dark:text-green-400"
                    : "border-blue-500 text-blue-700 dark:text-blue-400"
                }
              >
                {channel === "whatsapp" ? "WhatsApp" : "SMS"}
              </Badge>
            </div>
          )}

          {/* Warning for contacts without phone */}
          {contactsWithoutPhone.length > 0 && selectedTemplate && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {contactsWithoutPhone.length} contact
                {contactsWithoutPhone.length !== 1 ? "s" : ""} will be skipped
                (no phone number):
                <span className="font-medium ml-1">
                  {contactsWithoutPhone
                    .slice(0, 3)
                    .map((c) => `${c.first_name} ${c.last_name}`)
                    .join(", ")}
                  {contactsWithoutPhone.length > 3 && "..."}
                </span>
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={
              !templateId || contactsWithPhone.length === 0 || isPending
            }
          >
            <Send className="h-4 w-4" />
            {isPending
              ? "Sending..."
              : `Send to ${contactsWithPhone.length} contact${contactsWithPhone.length !== 1 ? "s" : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
